import { create } from 'zustand';
import axios from 'axios';

type NavigationOrder = 'folders_first' | 'documents_first';

interface AppConfig {
  appTitle: string;
  navigationOrder: NavigationOrder;
  accentColor: string;
  passwordResetEnabled: boolean;
}

interface OllamaConfig {
  enabled: boolean;
  model: string;
}

interface ConfigState {
  config: AppConfig;
  ollama: OllamaConfig;
  isLoaded: boolean;
  fetchConfig: () => Promise<void>;
  updateSetting: (key: string, value: string) => Promise<void>;
}

export const useConfigStore = create<ConfigState>((set) => ({
  config: {
    appTitle: import.meta.env.VITE_APP_TITLE || 'OverlookDocs', // Fallback to build-time default
    navigationOrder: 'folders_first',
    accentColor: '#9333ea', // Default purple color
    passwordResetEnabled: false,
  },
  ollama: {
    enabled: false,
    model: 'llama2',
  },
  isLoaded: false,

  fetchConfig: async () => {
    try {
      const response = await axios.get<{ 
        appTitle: string; 
        navigationOrder: NavigationOrder; 
        accentColor: string;
        ollamaEnabled: boolean;
        ollamaModel: string;
      }>('/api/config');
      set({ 
        config: {
          appTitle: response.data.appTitle,
          navigationOrder: response.data.navigationOrder,
          accentColor: response.data.accentColor,
          passwordResetEnabled: false,
        },
        ollama: {
          enabled: response.data.ollamaEnabled,
          model: response.data.ollamaModel,
        },
        isLoaded: true 
      });
    } catch (error) {
      console.error('Failed to fetch config:', error);
      // Keep the default/fallback config if fetch fails
      set({ isLoaded: true });
    }
  },

  updateSetting: async (key: string, value: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        '/api/settings',
        { key, value },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Update local config state
      set((state) => ({
        config: {
          ...state.config,
          [key === 'navigation_order' ? 'navigationOrder' : key]: value,
        },
      }));
    } catch (error) {
      console.error('Failed to update setting:', error);
      throw error;
    }
  },
}));
